import { useEffect, useState } from "react";

export const ONLINE_POLL_INTERVAL_MS = 1000;

/**
 * Tracks browser online/offline state via navigator.onLine and window events.
 * While offline, polls navigator.onLine so a missed `online` event cannot stick
 * the UI in the offline state.
 */
export const useBrowserOnline = (): boolean => {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (isOnline) {
      return;
    }

    const syncIfOnline = () => {
      if (navigator.onLine) {
        setIsOnline(true);
      }
    };

    syncIfOnline();
    const id = setInterval(syncIfOnline, ONLINE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isOnline]);

  return isOnline;
};
